export type QuickButtonSpec = {
    label: string;
    color?: string;
    backgroundColor?: string;
    requiredGroupId?: string;
};

export function parseQuickButtons(input: string): Array<QuickButtonSpec> {
    const lines = input
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);

    const parsedButtons: Array<QuickButtonSpec> = [];
    const requiredGroupStack: string[] = [];
    let requiredGroupCount = 0;

    for (const line of lines) {
        if (isRequiredStartTag(line)) {
            requiredGroupCount += 1;
            requiredGroupStack.push(`required-${requiredGroupCount}`);
            continue;
        }

        if (isRequiredEndTag(line)) {
            requiredGroupStack.pop();
            continue;
        }

        const parsed = parseAnyButtonLine(line) ?? { label: line };
        const currentRequiredGroupId = requiredGroupStack[requiredGroupStack.length - 1];

        parsedButtons.push({
            ...parsed,
            ...(currentRequiredGroupId ? { requiredGroupId: currentRequiredGroupId } : {}),
        });
    }

    return parsedButtons;
}

export type QuickButtonsValidationResult = {
    isValid: boolean;
    invalidValues: string[];
    missingRequiredGroups: string[][];
};

export function validateQuickButtonsMessage(message: string, quickButtons: Array<QuickButtonSpec>): QuickButtonsValidationResult {
    if (quickButtons.length === 0) {
        return {
            isValid: true,
            invalidValues: [],
            missingRequiredGroups: [],
        };
    }

    const messageValues = splitMessageValues(message);
    const quickButtonLabels = new Set(quickButtons.map((button) => button.label.trim()).filter(Boolean));
    const selectedQuickButtonLabels = new Set(messageValues.filter((value) => quickButtonLabels.has(value)));
    const requiredGroups = buildRequiredGroups(quickButtons);

    const missingRequiredGroups = requiredGroups
        .filter((labels) => labels.every((label) => !selectedQuickButtonLabels.has(label)))
        .map((labels) => [...labels]);

    return {
        isValid: missingRequiredGroups.length === 0,
        invalidValues: [],
        missingRequiredGroups,
    };
}

function parseAnyButtonLine(line: string): QuickButtonSpec | null {
    return (
        parseSelfClosingButton(line) ??
        parseWrappedButton(line)
    );
}

function parseSelfClosingButton(line: string): QuickButtonSpec | null {
    const match = line.match(/^\[button([^\]]*)\]$/);

    if (!match) return null;

    const attrs = match[1] ?? "";
    const label = readQuotedAttr(attrs, "label");

    if (!label) return null;

    return {
        label,
        color: readQuotedAttr(attrs, "color") ?? undefined,
        backgroundColor: readQuotedAttr(attrs, "backgroundColor") ?? undefined,
    };
}

function parseWrappedButton(line: string): QuickButtonSpec | null {
    const match = line.match(/^\[button([^\]]*)\]([\s\S]*?)\[\/button\]$/);

    if (!match) return null;

    const attrs = match[1] ?? "";
    const inner = (match[2] ?? "").trim();

    if (!inner) return null;

    return {
        label: inner,
        color: readQuotedAttr(attrs, "color") ?? undefined,
        backgroundColor: readQuotedAttr(attrs, "backgroundColor") ?? undefined,
    };
}

function readQuotedAttr(attrs: string, name: string): string | null {
    const re = new RegExp(`\\b${name}="([^"]*)"`);
    const m = attrs.match(re);

    if (!m) return null;

    return m[1] ?? null;
}

function isRequiredStartTag(line: string): boolean {
    return /^\[required\]$/i.test(line);
}

function isRequiredEndTag(line: string): boolean {
    return /^\[\/required\]$/i.test(line);
}

function splitMessageValues(message: string): string[] {
    return message
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
}

function buildRequiredGroups(quickButtons: Array<QuickButtonSpec>): string[][] {
    const grouped = new Map<string, string[]>();

    quickButtons.forEach((button) => {
        if (!button.requiredGroupId) return;

        const group = grouped.get(button.requiredGroupId) ?? [];
        group.push(button.label);
        grouped.set(button.requiredGroupId, group);
    });

    return Array.from(grouped.values()).map((group) => uniqueValues(group));
}

function uniqueValues(values: string[]): string[] {
    return Array.from(new Set(values));
}

export function parseQuickButtonsForString(message: string, quickButtons: Array<QuickButtonSpec>): string {
    if (quickButtons.length === 0) {
        return message;
    }

    const messageValues = splitMessageValues(message);
    const quickButtonMap = new Map(quickButtons.map((button) => [button.label.trim(), button]));

    const renderedValues = messageValues.map((value) => {
        const button = quickButtonMap.get(value);
        if (!button) {
            return value;
        }

        const colorStyle = button.color ? `color: ${button.color};` : "";
        const backgroundColorStyle = button.backgroundColor ? `background-color: ${button.backgroundColor}; border-color: ${button.backgroundColor};` : "";

        if(!colorStyle && !backgroundColorStyle) {
            return value;
        }

        return `<span style="${colorStyle} ${backgroundColorStyle} padding: 2px 4px; border-radius: 4px; border: 1px solid transparent; display: inline-block;">${value}</span>`;
    });

    return renderedValues.join("\n");
}
